import { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { Button } from 'primereact/button';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import flyerBgImg from '../../../Images/flyerBg.webp';
import rightFlyerImg from '../../../Images/rightFlyerImg.png';
import headShotImg from '../../../Images/headShotImg.png';
import qrCodeImg from '../../../Images/qrCodeImg.png';
import flyerPhoneIcon from '../../../Images/flyerPhoneIcon.png';
import flyerHomeIcon from '../../../Images/flyerHomeIcon.png';
import flyerSecondPageBg from '../../../Images/flyerSecondPageBg.png';
import flyerLogo from '../../../Images/flyerLogo.png';
import flyerLogoDark from '../../../Images/flyerLogoDark.png';
import FlexComponent from '../../FlexComponent';
import { aktivGroteskRegular } from '../../../utils/aktivGroteskRegular';
import { aktivGroteskBold } from '../../../utils/aktivGrotesk';
import { aktivGroteskLight } from '../../../utils/aktivGroteskLight';
import { ProgressSpinner } from 'primereact/progressspinner';

const PX_TO_MM = 0.264583;
const pageWidth = 210; // A4 width in mm
const defaultPageHeight = 280; // A4 height in mm
const marginLeft = 12;

function FlyerPdf({ flyerData }) {
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const addBackgroundImage = async (doc) => {
    const bgImg = new Image();
    bgImg.src = flyerBgImg;
    await new Promise((resolve, reject) => {
      bgImg.onload = resolve;
      bgImg.onerror = reject;
    });
    doc.addImage(bgImg, 'WEBP', 0, 0, pageWidth, 90, null, 'FAST');
  };

  const addLogo = async (doc) => {
    const logoImg = new Image();
    logoImg.src = flyerLogo;
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
    });
    doc.addImage(logoImg, 'PNG', marginLeft, 14, 25, 7, null, 'FAST');
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // Handle CORS if needed
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });
  };

  const generatePDF = async (callback) => {
    const doc = new jsPDF({
      unit: 'mm',
      format: [pageWidth, defaultPageHeight],
      compress: true,
    });

    doc.addFileToVFS('aktivGrotesk-Regular.ttf', aktivGroteskRegular);
    doc.addFileToVFS('aktivGrotesk-Bold.ttf', aktivGroteskBold);
    doc.addFileToVFS('aktivGrotesk-Light.ttf', aktivGroteskLight);
    doc.addFont('aktivGrotesk-Regular.ttf', 'Aktiv', 'normal');
    doc.addFont('aktivGrotesk-Bold.ttf', 'AktivBold', 'bold');
    doc.addFont('aktivGrotesk-Light.ttf', 'AktivLight', 'normal');

    await addBackgroundImage(doc);
    await addLogo(doc);

    let cursorY = 36;

    // Title and Highlight
    doc.setFontSize(13);
    doc.setFont('Aktiv', 'normal');
    doc.setTextColor('white');
    doc.text('DSCR PROGRAM', marginLeft, (cursorY += 8));
    doc.setFont('AktivLight', 'normal');
    doc.setFontSize(40);
    doc.text('Investors can buy with', marginLeft, (cursorY += 12));
    doc.setFillColor('#005896');
    doc.rect(marginLeft - 2, cursorY + 2, 144, 16, 'F');
    doc.setFont('AktivBold', 'bold');
    doc.setFontSize(40);
    // Use flyerData.image or fallback to default headShotImg
    if(flyerData?.image){
      const headShot = flyerData?.image || headShotImg;
      doc.addImage(headShot, 'PNG', 155, cursorY, 50, 60, null, 'FAST', 0.7);
    }
    doc.text('no proof of income.*', marginLeft, (cursorY += 14));
    
    // Use flyerData.qr_code or fallback to default qrCodeImg
    if(flyerData?.qr_code){
      doc.setFillColor('#005896');
      doc.rect(183.5, cursorY + 30, 23, 24, 'F');
      const qrCode = await loadImage(flyerData?.qr_code);
      doc.addImage(qrCode, 'PNG', 185, cursorY + 32, 20, 20, null, 'FAST', 0.8);
    }

    cursorY += 40;

    // Program Description
    doc.setFontSize(19);
    doc.setFont('AktivBold', 'bold');
    doc.setTextColor('#005896');
    doc.text('The United DSCR Program', marginLeft, cursorY);
    cursorY += 8;

    doc.setFontSize(12);
    doc.setTextColor('#4D4C4D');
    doc.setFont('Aktiv', 'normal');
    doc.text('Debt Service Coverage Ratio loans enable investors to', marginLeft, (cursorY += 6));
    doc.text('purchase investment properties without need for proof', marginLeft, (cursorY += 6));
    doc.text('of income or tax returns, as long as the projected rent', marginLeft, (cursorY += 6));
    doc.text('covers some or all of the proposed mortgage payment.', marginLeft, (cursorY += 6));

    cursorY += 10;
    doc.setTextColor('white');
    // Program Highlights
    doc.setFillColor('#19254F');
    doc.rect(marginLeft - 2, cursorY, 70, 10, 'F');
    doc.text('PROGRAM HIGHLIGHTS', marginLeft + 2, cursorY + 7);
    cursorY += 20;

    doc.setTextColor('#4D4C4D');
    doc.setFontSize(10);
    doc.text('• Loan amount up to $3 Million', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• Max cash-in-hand $1 Million, no limit for CLTV <65%', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• 30 & 40 Year Fixed, and ARM options available', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• No income, no employment', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• Up to 80% CLTV', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• Min. FICO 620', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• Eligible for Non-Permanent Residents and Foreign Nationals', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• Ownership of any Property within the past 24 months', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• 1-4 Units, Condotels allowed', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• Mixed-use and multi-family (5-8 units) allowed', marginLeft, cursorY);
    cursorY += 6;
    doc.text('• Min Borrower Contribution is 20%', marginLeft, cursorY);
    cursorY += 10;

    // Contact Info (Dynamic)
    doc.setFontSize(12);
    cursorY -= 102;
    doc.text(`${flyerData?.first_name || ''} ${flyerData?.last_name || ''}`, 125, cursorY);
    cursorY += 5;
    doc.text(flyerData?.designation || 'N/A', 125, cursorY);
    cursorY += 5;
    doc.text(flyerData?.nmls_id ? `NMLS ID ${flyerData.nmls_id}` : 'NMLS ID N/A', 125, cursorY);
    cursorY += 5;
    doc.text(flyerData?.email || 'N/A', 125, cursorY);
    if(flyerData?.branch?.image){
      const branchImg = await loadImage(flyerData?.branch?.image);
      doc.addImage(branchImg, 'PNG', 125, (cursorY += 5), 70, 30, null, 'FAST', 0.7);
    }

    cursorY += 40;

    // Company Info (Dynamic)
    doc.text(flyerData?.company || 'United Mortgage Corp', 125, cursorY);
    cursorY += 5;
    doc.text(flyerData?.branch?.nmls_id ? `Branch NMLS ID ${flyerData.branch?.nmls_id}` : 'Branch NMLS ID N/A', 125, cursorY);
    cursorY += 5;
    doc.text(flyerData?.branch?.address ? `${flyerData.branch?.address}` : 'N/A', 125, cursorY);
    cursorY += 5;
    doc.text(`${flyerData?.branch?.city}, ${flyerData?.branch?.state} ${flyerData?.branch?.zip}`, 125, cursorY);
    cursorY += 10;

    doc.setFont('AktivBold', 'bold');
    doc.setFontSize(21);
    doc.setFillColor('#CDCCCD');
    doc.roundedRect(125, (cursorY += 2), 75, 25, 2, 2, 'F');
    doc.setFillColor('white');
    doc.roundedRect(125.5, (cursorY += 0.5), 74, 24, 2, 2, 'F');

    // Phone and QR Code
    doc.setTextColor('black');
    doc.addImage(flyerPhoneIcon, 'PNG', 134, cursorY + 4, 4, 6, null, 'FAST', 0.8);
    doc.text('551.486.3376', 140, (cursorY += 10)); // Replace with flyerData.phone if available
    doc.setFontSize(12);
    doc.setFont('Aktiv', 'normal');
    doc.text('Call or text to learn more.', 142, (cursorY += 7));

    doc.setFontSize(8);
    doc.text(
      `${flyerData?.full_name || 'N/A'}, ${flyerData?.designation || 'Loan Officer'}, NMLS ID ${flyerData?.nmls_id || 'N/A'} | ${flyerData?.company || 'United Mortgage Corp.'}, NMLS ID 1330 \nFor licensing information visit nmlsconsumeraccess.org. All applicants subject to credit and underwriting approval.`,
      30,
      (cursorY += 20)
    );
    doc.addImage(flyerHomeIcon, 'PNG', 20, cursorY - 3, 10, 8, null, 'FAST', 0.8);

    doc.addPage();
    cursorY = 15;

    doc.addImage(flyerSecondPageBg, 'PNG', 0, cursorY, pageWidth, 265, null, 'FAST', 0.6);
    doc.setFontSize(37);
    doc.setFont('Times', 'bold');
    doc.text('Buying a home', 65, (cursorY += 60));
    doc.text('begins here.', 70, (cursorY += 12));
    doc.addImage(flyerLogoDark, 'PNG', 75, (cursorY += 80), 60, 30, null, 'FAST', 0.7);

    doc.setDrawColor(0, 0, 0);
    doc.line(10, cursorY + 52, 200, cursorY + 52);
    doc.setFontSize(7);
    doc.setFont('AktivLight', 'normal');
    doc.text(
      'This is not a credit decision or a commitment to lend. Rates and programs subject to change without notice and may not be available at commitment or closing.',
      18,
      (cursorY += 60)
    );
    doc.addImage(flyerHomeIcon, 'PNG', 8, cursorY - 3, 10, 8, null, 'FAST', 0.8);
    doc.text(
      'Subject to approval. *Some of the above loan products that do not require documentation may have higher interest rates, more points or more fees than other',
      18,
      (cursorY += 6)
    );
    doc.text(
      `loan products requiring documentation and are not available in AK, CA, CO, HI, MA, MD, MT, NM, ND, OH, OR, SD, UT, VT and WA. ${flyerData?.company || 'United Mortgage Corp.'}, NMLS`,
      18,
      (cursorY += 6)
    );
    doc.text(
      'ID# 1330, 401 Broadhollow Road, Suite 150, Melville, NY 11747, 800-462-4862. United Mortgage Corp. is not affiliated with the U.S. government, HUD, FHA, VA, or',
      18,
      (cursorY += 6)
    );
    doc.text(
      'any other government agencies. Licensed by the N.J. Department of Banking and Insurance, Residential Mortgage Lender License #9909065 / RMLA-Licensed',
      18,
      (cursorY += 6)
    );
    doc.text(
      'Mortgage Servicer Registration. For licensing information go to: unitedmortgage.com/licensing and nmlsconsumeraccess.org. Equal Housing Opportunity. SM',
      18,
      (cursorY += 6)
    );
    doc.text('2024 United Mortgage Corp. All rights reserved.', 18, (cursorY += 6));

    callback(doc);
  };

  const savePdf = (callback) => {
    generatePDF((doc) => {
      const pdfBlob = doc.output('blob');
      setPdfData(pdfBlob);
      callback(pdfBlob);
    });
  };

  const handleSavePdf = () => {
    setPdfLoading(true);
    savePdf((pdfBlob) => {
      const formDataObj = new FormData();
      formDataObj.append('pdf', pdfBlob, 'flyer.pdf');
      formDataObj.append('userId', flyerData?.id || 123);
      console.log(formDataObj);
      setPdfLoading(false);
    });
  };

  const previewPdf = () => {
    setPdfLoading(true);
    generatePDF((doc) => {
      const pdfBlob = doc.output('blob');
      const pdfURL = URL.createObjectURL(pdfBlob);
      setPdfBlobUrl(pdfURL);
      setPdfLoading(false);
    });
  };

  const downloadPdf = () => {
    // setPdfLoading(true);
    generatePDF((doc) => {
      doc.save(`${flyerData?.full_name || 'flyer'}.pdf`);
      // setPdfLoading(false);
    });
  };

  // Trigger PDF preview when dialog opens or flyerData changes
  useEffect(() => {
    if (flyerData) {
      previewPdf();
    }
    // Clean up Blob URL on unmount
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl('');
      }
    };
  }, [flyerData]);

  return (
    <div>
      <FlexComponent justifyContent="center">
        <div className="previewPdfContainer" style={{ margin: '1rem', background: 'white', width: '100%' }}>
          {/* <Button onClick={previewPdf}>Preview</Button> */}
          {/* <Button onClick={handleSavePdf} style={{ marginLeft: '1rem' }}>Save</Button> */}
          <FlexComponent justifyContent="end" style={{ padding: ".5rem" }} >
            <Button onClick={downloadPdf} icon="pi pi-download" style={{ padding: ".5rem" }}/>
          </FlexComponent>
          {pdfBlobUrl && !pdfLoading && (
            <div >
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer fileUrl={pdfBlobUrl}
                // plugins={[defaultLayoutPluginInstance]} 
                />
              </Worker>
            </div>
          )}
          {pdfLoading && (<FlexComponent justifyContent="center" style={{minHeight: "80vh"}} >
            <div style={{color: "green", padding: "1rem", fontSize: "1.5rem"}} >Loading...</div>
          </FlexComponent>)}
        </div>
      </FlexComponent>
    </div>
  );
}

export default FlyerPdf;